import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../styles/marketer/MarketerSidebar.css';

// SVG Icons
const ChevronDown = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
  </svg>
);

// 그룹 순서 확정(2026-07-27): 광고 프로젝트 → 포인트 관리 → 내 정보 관리 → 고객센터
const MENU_GROUPS = [
  {
    key: 'project',
    label: '광고 프로젝트 (Ad Projects)',
    items: [
      ['prjCreate', '인플루언서 모집하기 (프로젝트 생성) (Create Project)'],
      ['prjSelect', '인플루언서 선정하기 (Select Influencers)'],
      ['prjResult', '결과물 확인하기 (Review Deliverables)'],
      ['prjCpsCharge', 'CPS 정산하기 (CPS Settlement)'],
      ['prjOn', '진행중인 프로젝트 보기 (Ongoing Projects)'],
      ['prjFinish', '완료 프로젝트 성과 보기 (Completed Projects)'],
      ['prjInfluencerStats', '인플루언서 단가 통계 보기 (Rate Statistics)'],
    ],
  },
  {
    key: 'point',
    label: '포인트 관리 (Points)',
    items: [
      ['pointCharge', '포인트 충전하기 (Top up Points)'],
      ['pointList', '포인트 이용내역 (Point History)'],
      ['pointRefund', '포인트 환불신청 (Request Refund)'],
    ],
  },
  {
    key: 'my',
    label: '내 정보 관리 (My Info)',
    items: [
      ['myInfo', '계정 정보 관리 (Account Info)'],
      ['password', '비밀번호 변경 (Change Password)'],
    ],
  },
  {
    key: 'inquiry',
    label: '고객센터 (Support)',
    items: [
      ['inqOn', '진행중인 문의 (Open Inquiries)'],
      ['inqFinish', '완료된 문의 (Closed Inquiries)'],
    ],
  },
];

const MarketerSidebar = ({ isOpen, onClose }) => {
  const nav = useNavigate();
  const location = useLocation();
  const [openSection, setOpenSection] = useState(null); // 'project', 'point', 'my', 'inquiry'

  // URL 파라미터 기반 상태 동기화
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setOpenSection(tab);
    }
  }, [location.search]);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const closeOnMobile = () => {
    if (window.innerWidth <= 1024 && onClose) {
      onClose(); // 모바일에서 메뉴 클릭 시 사이드바 닫기
    }
  };

  const handleMenuClick = (tab, sub) => {
    nav(`/marketer/mybiz?tab=${tab}&sub=${sub}`);
    closeOnMobile();
  };

  const isActive = (tab, sub) => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') === tab && params.get('sub') === sub;
  };

  const isHomeActive = (() => {
    const params = new URLSearchParams(location.search);
    const sub = params.get('sub');
    return !sub || sub === 'home';
  })();

  return (
    <div className={`mb-sidebar-area ${isOpen ? 'open' : ''}`}>
      <div className="mb-sidebar">
        {/* 프로필 영역 */}
        <div className="mb-sidebar-profile">
          <h3>광고주 센터 (Advertiser Center)</h3>
          <p>Marketer Workspace</p>
        </div>

        {/* 네비게이션 */}
        <nav className="mb-sidebar-nav">

          {/* 비즈 홈 */}
          <div className="mb-nav-group">
            <button
              className={`mb-menu-item mb-menu-item--single ${isHomeActive ? 'active' : ''}`}
              onClick={() => handleMenuClick('home', 'home')}
            >
              <span>비즈 홈 (Business Home)</span>
            </button>
          </div>

          {/* 접이식 그룹들 */}
          {MENU_GROUPS.map((group) => (
            <div className="mb-nav-group" key={group.key}>
              <button
                className={`mb-menu-item ${openSection === group.key ? 'active' : ''}`}
                onClick={() => toggleSection(group.key)}
              >
                <span>{group.label}</span>
                <ChevronDown className={`mb-menu-chevron ${openSection === group.key ? 'rotate' : ''}`} />
              </button>
              <div className={`mb-submenu ${openSection === group.key ? 'open' : ''}`}>
                <div className="mb-submenu__inner">
                  {/* 고객센터: 문의하기(작성)가 맨 위 */}
                  {group.key === 'inquiry' && (
                    <button
                      className="mb-submenu-item"
                      onClick={() => {
                        nav('/inquiry', { state: { role: 'marketer' } });
                        closeOnMobile();
                      }}
                    >
                      문의하기 (Contact Us)
                    </button>
                  )}
                  {group.items.map(([sub, label]) => (
                    <button
                      key={sub}
                      className={`mb-submenu-item ${isActive(group.key, sub) ? 'active' : ''}`}
                      onClick={() => handleMenuClick(group.key, sub)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* 서비스 안내 — 여백이 있으면 하단 고정(margin-top:auto), 위 그룹 확장 시엔 밀려나 스크롤로 접근 */}
          <div className="mb-nav-group mb-nav-group--bottom">
            <div className="mb-menu-item" style={{ cursor: 'default' }}>
              <span>서비스 안내 (Service Info)</span>
            </div>
            <div className="mb-submenu open">
              <div className="mb-submenu__inner">
                <button
                  className="mb-submenu-item"
                  onClick={() => {
                    nav('/lp/marketer');
                    closeOnMobile();
                  }}
                >
                  광고주 안내 (For Advertisers)
                </button>
                <button
                  className="mb-submenu-item"
                  onClick={() => window.open('/guide', '_blank', 'noopener,noreferrer')}
                >
                  서비스 가이드 (Service Guide) ↗
                </button>
                <button
                  className="mb-submenu-item"
                  onClick={() => {
                    nav('/faq');
                    closeOnMobile();
                  }}
                >
                  FAQ
                </button>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default MarketerSidebar;
